/* eslint-disable jira/import-whitelist */
import { loadQuery, type PreloadedQuery } from 'react-relay';
import {
	DEFAULT_LIMIT,
	filterInitialState,
	filterAliasMap,
} from '@jira/router-resources__projects-directory-v3-utils/src/filterFields.constants.tsx';
import {
	mapQueryToFilter,
	sanitizeLegacyQuery,
} from '@jira/router-resources__projects-directory-v3-utils/src/sanitizeLegacyQuery.tsx';
import ProjectDirectoryPageQuery, {
	type ProjectDirectoryLayoutQuery,
} from '@jira/global-pages__directories-projects-directory-v3/src/ui/page-layout/__generated__/ProjectDirectoryLayoutQuery.graphql';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { RELAY_RESOURCE_TYPE } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { QueryPromisesMap } from '@atlassian/relay-query-promises';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';

const RESOURCE_TYPE_PROJECTS_DIRECTORY_RELAY = 'PROJECTS_DIRECTORY_RELAY';

export const projectsDirectoryPageResource: RouteResource<
	PreloadedQuery<ProjectDirectoryLayoutQuery, Record<string, unknown>>
> = createResource<PreloadedQuery<ProjectDirectoryLayoutQuery>>({
	type: `${RELAY_RESOURCE_TYPE}_${RESOURCE_TYPE_PROJECTS_DIRECTORY_RELAY}`,
	getKey: () => 'projects-directory-relay',
	// @ts-expect-error - might return null
	getData: async ({ query, route }, { tenantContext }) => {
		const { cloudId, isAnonymous } = tenantContext;

		// SSR/AGG does not currently work for anonymous users and returns a 403. We should temporally disable anonymous SSR until
		// the underlying auth issues have been fixed. Duplo team will push the real fix later.
		if (__SERVER__ && isAnonymous) {
			return null;
		}

		const queryReference = loadQuery(
			getRelayEnvironment(),
			ProjectDirectoryPageQuery,
			{
				cloudId: tenantContext.cloudId,
				filter: mapQueryToFilter(filterInitialState, {
					cloudId,
					query: sanitizeLegacyQuery(query),
					aliases: filterAliasMap,
				}),
				first: query?.page ? DEFAULT_LIMIT * (parseInt(query.page, 10) || 1) : DEFAULT_LIMIT,
				last: DEFAULT_LIMIT,
				isAdminSettingsContext: route.name === 'projects-directory-manage',
				isAnonymous,
				isSpaceAgentsNonAdminViewEnabled:
					// eslint-disable-next-line jira/ff/no-preconditioning
					fg('space-agent-settings-allow-non-admin-view') &&
					expVal('yamato-agents-home-space-settings-beta', 'isEnabled', false),
			},
			{
				fetchPolicy: 'network-only',
			},
		);

		if (__SERVER__ && queryReference.id != null) {
			await QueryPromisesMap.get(queryReference.id);
		}

		return queryReference;
	},
});
